import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useProject } from "@/lib/project";
import { keys } from "@/lib/queries";
import type { Task, TaskListItem } from "@/lib/types";

export function useNow(intervalMs = 1000, enabled = true) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return;
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs, enabled]);
  return now;
}

type TaskPatch = Partial<Pick<Task, "title" | "epicId" | "sprintId" | "stageId" | "position">> & { body?: unknown[] };

/** Task mutations with optimistic cache updates for list and detail views. */
export function useTaskActions() {
  const { pid, stageById } = useProject();
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: keys.project(pid) });
  const fail = (err: unknown) => window.dispatchEvent(new CustomEvent("app:error", { detail: err }));

  const patchCaches = (id: string, fn: <T extends TaskListItem | Task>(t: T) => T) => {
    qc.setQueriesData<TaskListItem[]>({ queryKey: ["p", pid, "tasks"] }, (list) => list?.map((t) => (t.id === id ? fn(t) : t)));
    qc.setQueryData<Task>(keys.task(pid, id), (t) => (t ? fn(t) : t));
  };

  return {
    update: async (id: string, patch: TaskPatch, { silent = false } = {}) => {
      const { body, ...listPatch } = patch;
      if (!silent) {
        // cancel in-flight refetches synchronously so they can't overwrite the optimistic update
        void qc.cancelQueries({ queryKey: ["p", pid, "tasks"] });
        void qc.cancelQueries({ queryKey: keys.task(pid, id) });
        patchCaches(id, (t) => {
          const next = { ...t, ...listPatch };
          const stage = patch.stageId ? stageById.get(patch.stageId) : undefined;
          const now = new Date().toISOString();
          if (stage?.kind === "in_progress" && !next.inProgressAt) next.inProgressAt = now;
          if (stage?.kind === "qa" && !next.qaReachedAt) next.qaReachedAt = now;
          return next;
        });
      }
      try {
        await api.updateTask(pid, id, body !== undefined ? { ...listPatch, body } : listPatch);
      } catch (err) {
        fail(err);
      } finally {
        if (!silent) refresh();
      }
    },
    setValue: async (id: string, propertyId: string, values: string[]) => {
      void qc.cancelQueries({ queryKey: ["p", pid, "tasks"] });
      patchCaches(id, (t) => ({ ...t, values: { ...t.values, [propertyId]: values } }));
      try {
        await api.setValue(pid, id, propertyId, values);
      } catch (err) {
        fail(err);
      } finally {
        refresh();
      }
    },
    run: async (p: Promise<unknown>) => {
      try {
        await p;
      } catch (err) {
        fail(err);
      } finally {
        await refresh();
      }
    },
  };
}
